import { ReturnFrequency } from 'store/api/enums';
import {
  AccountStatus,
  AccountType,
  AdvancedUserRightsCodes,
  EntityStatus,
  ProviderPlan,
} from 'store/app/enums';
import { User } from 'store/auth/models';
import { CoaAccountRow } from 'store/chart-of-accounts/models';
import {
  MappedReturn,
  ReturnType,
  Section,
  TaxRate,
} from 'store/returns/models/returnData';
import { ActivityLog, VatReturnLog } from 'store/vat/models';

/** A selectable value under a tracking dimension (e.g. Xero tracking option). */
export type TrackingCategoryOption = {
  id: string;
  name: string;
};

/** Provider tracking dimension with nested options (matches `/tracking/categories` payload). */
export type TrackingCategory = {
  id: string;
  name: string;
  options: TrackingCategoryOption[];
};

export type OrganisationsAndMappings = {
  showActions: boolean;
  showConnections: boolean;
  organisations: Organisation[];
  mappings: Mapping[];
};

export type Organisation = {
  legalName: string;
  friendlyName: string;
  connectionId: string;
  country: string;
  createTimestamp: string | number;
  creator: string;
  currency: string;
  isGroup: boolean | null;
  taxNumber: string;
  uuid: string;
  version: string | number;
  providerName: string;
  /** Provider subscription/plan tier (e.g. Xero LEDGER). */
  providerPlan?: ProviderPlan | null;
  shortCode: string;
  taxRates: TaxRate[];
  taxRatesUnique: TaxRate[];
  chartOfAccounts?: CoaAccountRow[];
  chartOfAccountsLoaded?: boolean;
  taxEnabled: boolean;
  returnTypes: ReturnType[];
  returnTypesEdit: ReturnType[];
  users: User[];
  connectedProperly: boolean;
  vatCheckerActive: boolean;
  operationRights: OperationRight[];
  organisationDetails: OrganisationDetails | null;
  status: EntityStatus;
  excelFilesMetadata?: ExcelFileMetadata[];
  /** Excel/transactions-related activity timeline for this organisation. */
  activityLogs?: ActivityLog[];
  /** Tracking categories from provider (e.g. Xero dimensions) keyed by organisation fetch. */
  trackingCategories?: TrackingCategory[];
  /** Transaction items from provider (e.g. Xero items) keyed by organisation fetch. */
  transactionItems?: TransactionItem[];
  hasTaxRates?: boolean;
  groupedConnections?: GroupedConnection[];
  hmrcConnectionUuid?: string | null;
  scanReceiptEmail?: string;
  receiptScanStepsCompleted?: boolean;
};

export type GroupedConnection = {
  clientCode: string | null;
  conShortCode: string;
  connectionId: string;
  createTimestamp: number;
  currency: string;
  deletedDate: number | null;
  demoCompany: boolean;
  lastUsed: number;
  lastValidated: number;
  legalName: string;
  name: string;
  officeLocation: string | null;
  providerId: string;
  providerName: string;
  taxNumber: string;
  uuid: string;
};
export type OrganisationAttachment = {
  uuid: string;
  fileName: string;
  generationDate: string;
  description: string;
  url?: string;
};

export type OperationRight = {
  activatedAt: string;
  isActive: boolean;
  operationDescription: string;
  operationName: string;
  revokedAt: string | null;
  uuid: string;
  operationRightCode: AdvancedUserRightsCodes;
};

export type Mapping = {
  accountId: string;
  canDelete: boolean;
  cashAccounting: boolean;
  createdBy: string;
  createdDate: Date;
  jsonMapping: string;
  mappingId: string;
  mappingName: string;
  nextPeriod: string;
  organisationId: string;
  periodFromDate?: Date;
  returnDueDate?: Date;
  returnFrequency?: ReturnFrequency;
  returnTypeId: string;
  dataLogs?: VatReturnLog[];
  sections: Section[];
  /** HMRC MTD link for UK VAT returns; when false, show "Connect with HMRC". */
  hmrcConnected?: boolean;
  dueDate?: Date;
};

export type OrganisationDetails = {
  alternateName: string;
  code: string;
  contactPersonName: string;
  contactPersonEmail: string;
  contactPersonTelephone: string;
  registeredAddress: string;
  notes: string;
  attachments: OrganisationAttachment[];
  totalTaxBalance: number;
};

export type EditOrganisationParams = {
  entityName?: string;
  currency?: string;
  taxNumber?: string;
  country?: string;
  newName?: string;
  alternateName?: string;
  clientCode?: string;
  refId?: string;
  officeLocation?: string;
  code?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonTelephone?: string;
  registeredAddress?: string;
  notes?: string;
};

export type NewOrganisationParams = {
  providerName: string;
  legalName: string;
  name: string;
  currency: string;
  countryCode: string;
  scope: string;
  taxNumber: string;
  alternateName?: string;
  code?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonTelephone?: string;
  registeredAddress?: string;
  notes?: string;
};

export type UploadOrganisationAttachmentParams = {
  organisationId: string;
  description: string;
  file: File;
};

export type Entity = {
  providerName: string;
  organisationId: string;
  organisation: string;
  vatNumber: string;
  country: string;
  currency: string;
  groupedEntityIds?: string[];
  shortCode: string;
  returns: MappedReturn[];
  users: User[];
  key: number;
  connectionId: string;
  returnTypes?: ReturnType[];
  returnTypesEdit?: ReturnType[];
  taxRates: TaxRate[];
  connectedProperly: boolean;
  operationRights: OperationRight[];
  entityStatus: EntityStatus;
  hasTaxRates?: boolean;
  receiptScanStepsCompleted?: boolean;
};

export type EntityGroupData = {
  groupName: string;
  country: string;
  vatNumber: string;
  currency: string;
  organisationIds: string[];
};

export type EntityAccount = {
  accountID: string;
  organizationName: string;
  code: string;
  name: string;
  description: string;
  status: AccountStatus;
  type: AccountType | string;
  openingBalance: number;
  isSystemAccount: boolean;
  vatControlAccountType: string;
};

export type SelectedEntityAccounts = {
  connectionId: string;
  entityAccounts: EntityAccount[];
};

export type TaxRateTemplateToSave = {
  name: string;
  rates: string[];
};

export type TemplateTaxRate = {
  id: string;
  name: string;
  rate: number;
};

export type TaxRateTemplate = {
  id: string;
  name: string;
  rates?: any;
  tokenId: string;
  taxRates: TemplateTaxRate[];
  createdDate: string;
};

export enum ReviewStatus {
  NONE = 'NONE',
  NOT_REVIEWED = 'NOT_REVIEWED',
  REVIEWED = 'REVIEWED',
}

export interface ExcelTransaction {
  accountId: string | null;
  accountName: string | null;
  contactHashRow: string | null;
  contactId: string | null;
  contactName: string;
  contactType?: string | null;
  country: string;
  currency: string;
  description: string;
  discountAmount?: number | null;
  quantity?: number | null;
  unitPrice?: number | null;
  dueDate?: string | number | null;
  exchangeRate?: number | null;
  gross: number;
  hashRow: string | null;
  importId?: string | null;
  applicationConnectionId?: string | null;
  itemId?: string | null;
  locked: boolean;
  net: number;
  posted?: boolean | null;
  reference: string;
  sourceType: string;
  tax: number;
  taxName: string;
  taxRate: number;
  taxRateHashRow?: string | null;
  taxRateId: string | null;
  taxType?: string | null;
  transactionDate: string | number;
  transactionNumber: string;
  uploadDate?: string | number | null;
  uuid: string | null;
  vatNumber: string | null;
  accountCode?: string | null;
  accountType: string | null;
  hasAttachment: boolean | null;
  reviewStatus: ReviewStatus;
  /** Selected tracking category id/name for dimension 1 / 2 (third-party providers). */
  trackingCategory1Id?: string | null;
  trackingCategory1Name?: string | null;
  trackingCategory2Id?: string | null;
  trackingCategory2Name?: string | null;
  /** Selected tracking option id/name for dimension 1 / 2 (third-party providers; options from org tracking categories API). */
  trackingOption1Id?: string | null;
  trackingOption1Name?: string | null;
  trackingOption2Id?: string | null;
  trackingOption2Name?: string | null;
  /** True for legacy Excel rows (no review status from API); false/omitted for receipt review flow. */
  isExcelTransaction?: boolean;
  /** Soft-delete / reversal marker from API (e.g. NONE | DELETED). */
  reversalType?: string | null;
  /** When the journal row was created. */
  journalCreatedDate?: string | number | null;
  /** When the row was last non-financially edited; null if never set. */
  journalModifiedDate?: string | number | null;
  modifiedBy?: string | null;
  modifiedByUser?: Record<string, unknown> | null;
  /**
   * Set after a financial edit (amount/account/tax/date) that created a linked
   * reversal/replacement row; also set when deleted.
   */
  linkedTransactionId?: string | null;
  taxInclusive?: boolean;
}

export interface ValidatedExcelTransaction extends ExcelTransaction {
  errorReason: string;
}

export type UploadExcelTransactionsValidationData =
  | {
      importId?: string;
      organisationId?: string;
      hasDuplicates?: boolean;
      applicationConnectionId?: string;
      validationErrors?: ValidatedExcelTransaction[];
      successfulTransactions?: ValidatedExcelTransaction[];
    }
  | null
  | undefined;

export enum UploadSource {
  MANUAL = 'MANUAL',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  ONE_DRIVE = 'ONE_DRIVE',
  EMAIL = 'EMAIL',
}

/** Capture-from-email scanner status (populated by BE). */
export type EmailCaptureStatus = {
  email: string;
  connectedByUser?: { email?: string | null } | null;
  createdDate?: number | string | null;
};

export type ExcelFileMetadata = {
  accountDetailsId: string;
  excelMetadataId: string;
  fileName: string;
  fileType: string;
  importId: string;
  totalRows: number;
  uploadDate: number;
  status: UploadStatus;
  errorReason: string;
  user: User;
  uploadSource: UploadSource;
  isPosted: boolean;
  isReviewed: boolean;
  linkToProvider: string | null;
  reviewStatus?: ReviewStatus;
  /** When true, lines with the same tax code were combined into one transaction on scan. */
  grouped?: boolean;
};

export type ExcelTransactions = {
  content: ExcelTransaction[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  success: boolean;
  message: string;
};

export enum UploadStatus {
  WAITING_FOR_AI = 'WAITING_FOR_AI',
  AI_ERROR = 'AI_ERROR',
  BUILDING_TRANSACTIONS = 'BUILDING_TRANSACTIONS',
  ACTIVE = 'ACTIVE',
  INVALID_FILE = 'INVALID_FILE',
  AI_INVALID_FILE = 'AI_INVALID_FILE',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  MULTI_DOCUMENTS_FILE = 'MULTI_DOCUMENTS_FILE',
  TOO_MANY_DOCUMENTS = 'TOO_MANY_DOCUMENTS',
  UNSUPPORTED_CURRENCY = 'UNSUPPORTED_CURRENCY',
  DUPLICATED_DOCUMENT = 'DUPLICATED_DOCUMENT',
  EMPTY_DATE = 'EMPTY_DATE',
}

export type ReceiptUploadStatusFile = {
  importId: string;
  fileName: string;
  uploadDate: string;
  totalRows: number;
  fileType: string;
  accountDetailsId: string;
  status: UploadStatus;
  user: User | null;
  errorReason: string;
};

export type ReceiptUploadStatus = {
  createdDate: string;
  status: string;
  files: ReceiptUploadStatusFile[];
};

export type LastReceiptUploadStatusData = {
  status: ReceiptUploadStatus;
};

export type ProviderUploadReceiptResponse = {
  fileId: string;
  data: {
    id: string | null;
    link: string | null;
    success: boolean;
    errors: string[];
    message: string | null;
    validationErrors: any | null;
    postedAttachment: boolean;
    fileType: string | null;
  };
};

export type ReceiptEntityStatistics = {
  applicationConnectionId: string;
  uploadedFilesTotal: number;
  activeFiles: number;
  filesWithErrors: number;
  deletedFiles: number;
  uploadedManually: number;
  uploadedFromGoogleDrive: number;
  uploadedFromOneDrive: number;
  uploadedFromEmail: number;
  uploadedThisMonth: number;
  postedFiles: number;
  postedThisMonth: number;
  filesPendingReview: number;
  filesReviewed: number;
  transactionsPendingReview: number;
  reviewedTransactions: number;
};

export type ReceiptStatisticsTotals = Omit<
  ReceiptEntityStatistics,
  'applicationConnectionId'
>;

export type ReceiptStatisticsPayload = ReceiptStatisticsTotals & {
  entitiesStats: ReceiptEntityStatistics[];
};

export type ReceiptStatisticsDateRange = {
  from?: string;
  to?: string;
};

export type TransactionItem = {
  id: string;
  name: string;
};
